// MUST be first: Hermes lacks DOMException; livekit-client extends it at import time.
import './common/polyfills/domException';
import './common/polyfills/findNodeHandleWeb';
import '@expo/metro-runtime'; // for web hot reloading
import 'react-native-gesture-handler';
import 'expo-dev-client';
import './common/i18n';
import './common/polyfills/backHandlerLegacy';
import './common/polyfills/reactDomFindDomNode';

import React from 'react';
import { MathRendererProvider } from './common/components/MathRenderer';
import { GluestackUIProvider } from '@gluestack-ui/themed';
import { QueryClientProvider } from '@tanstack/react-query';
import * as Font from 'expo-font';
import * as SplashScreen from 'expo-splash-screen';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { LogBox, Platform } from 'react-native';

import AppLayout from './common/components/AppLayout';
import { EnvironmentGuard } from './common/components/EnvironmentGuard';
import ImpersonationBar from './common/components/ImpersonationBar';
import MainNavigation from './common/navigation/MainNavigation';
import { DialogProvider } from './common/context/DialogContext';
import { useAuthStore } from './common/auth/store';
import queryClient from './common/context/QueryClient';
import { TenantSettingsLoader, useTenantSettingsStore } from './common/context/TenantSettingsStore';
import MaintenanceGuard from './common/components/MaintenanceGuard';
import WhatsNewProvider from './common/whatsnew/WhatsNewProvider';
import { useColorModeStore, useSystemColorSchemeTracker } from './common/context/ColorModeStore';
import log from './common/utils/logger';
import { TOAST_PORTAL_POINTER_EVENTS_CSS } from './common/styles/toastPortalPointerEventsCss';
import { VisualLabApp } from './visual-lab/VisualLabApp';
import { VisualLabProviders } from './visual-lab/VisualLabProviders';

/**
 * =============================================================================
 * CRITICAL: "CLEAN ROOM" THEME INITIALIZATION
 * =============================================================================
 *
 * ThemeWrapper implements the "clean room" pattern for Gluestack-style v1.
 *
 * THE PROBLEM:
 * Gluestack-style v1 resolves component theme tokens (like $brandBase in Button.ts)
 * at createComponents() time, NOT at render time. If GluestackUIProvider mounts
 * before tenant settings load, components will "bake in" the default SNEXT colors
 * and ignore the tenant's brand colors.
 *
 * THE SOLUTION:
 * 1. TenantSettingsStore.gluestackConfig starts as NULL
 * 2. This component returns null while gluestackConfig is null (keeps splash visible)
 * 3. When tenant settings load, generateDynamicTheme() creates a fresh config
 *    with createComponents() using the tenant's token values
 * 4. Only THEN do we mount GluestackUIProvider with correctly-themed components
 * 5. The 'key' prop forces a full re-mount if tenant changes mid-session
 *
 * DO NOT:
 * - Remove the null check (if (!gluestackConfig) return null)
 * - Initialize gluestackConfig to a default value in TenantSettingsStore
 * - Mount GluestackUIProvider unconditionally
 *
 * If you break this pattern, buttons and other components will show default
 * SNEXT green (#A9D14C) instead of the tenant's brand color.
 *
 * Related files:
 * - TenantSettingsStore.ts (gluestackConfig must start as null)
 * - themeGenerator.ts (generateDynamicTheme creates fresh components)
 * =============================================================================
 */
const ThemeWrapper = ({ children }) => {
  const gluestackConfig = useTenantSettingsStore((state) => state.gluestackConfig);
  const themeVersion = useTenantSettingsStore((state) => state.themeVersion);
  const effectiveColorMode = useColorModeStore((state) => state.effectiveColorMode);

  // Track system color scheme changes - MUST be called before any early returns
  useSystemColorSchemeTracker();

  // CRITICAL: Do NOT remove this check! See "CLEAN ROOM" documentation above.
  // Returning null keeps the splash screen visible until tenant theme is ready.
  if (!gluestackConfig) {
    return null;
  }

  return (
    <GluestackUIProvider key={themeVersion} config={gluestackConfig} colorMode={effectiveColorMode}>
      {children}
    </GluestackUIProvider>
  );
};

import * as Sentry from '@sentry/react-native';
import Constants from 'expo-constants';
import {
  axiosFingerprint,
  meetingReconnectFingerprint,
  shouldDropSentryNoiseEvent,
} from './common/utils/sentryFingerprint';

// Prevent the splash screen from auto-hiding until fonts are loaded or error occurs
SplashScreen.preventAutoHideAsync();

Sentry.init({
  dsn: process.env.EXPO_PUBLIC_SENTRY_DSN,
  enabled: !__DEV__,
  // Capture 100% in dev, but only 20% in production to save quota
  tracesSampleRate: __DEV__ ? 1.0 : 0.2,
  debug: __DEV__,

  // Enable automatic breadcrumbs for better debugging
  integrations: [
    // React Navigation breadcrumbs (automatically tracks screen navigation)
    Sentry.reactNavigationIntegration(),
  ],

  // Filter out noisy, non-actionable errors
  beforeSend(event, hint) {
    const error = hint.originalException;
    const errorMessage = error instanceof Error ? error.message : String(error || '');
    
    // Filter out benign AbortErrors from media playback
    if (errorMessage.includes('AbortError') && (errorMessage.includes('play()') || errorMessage.includes('media resource'))) {
      return null;
    }

    // Filter out MathJax typesetting race condition
    if (errorMessage.includes('Typesetting failed') && errorMessage.includes('nextSibling')) {
      return null;
    }

    if (shouldDropSentryNoiseEvent(event, hint)) {
      return null;
    }

    const fp = axiosFingerprint(error) ?? meetingReconnectFingerprint(error);
    if (fp) {
      event.fingerprint = fp;
    }

    return event;
  },

  // Filter breadcrumbs to reduce noise
  beforeBreadcrumb(breadcrumb, hint) {
    // Skip successful HTTP requests (200, 201, 204) to reduce clutter
    if (breadcrumb.category === 'http' || breadcrumb.category === 'fetch') {
      const statusCode = breadcrumb.data?.status_code;
      if (statusCode && statusCode >= 200 && statusCode < 300) {
        return null; // Don't log successful requests
      }
    }

    // Redact sensitive data from URLs
    if (breadcrumb.data?.url) {
      breadcrumb.data.url = breadcrumb.data.url
        .replace(/token=[^&]+/gi, 'token=[REDACTED]')
        .replace(/password=[^&]+/gi, 'password=[REDACTED]')
        .replace(/Bearer\s+[^\s]+/gi, 'Bearer [REDACTED]');
    }

    return breadcrumb;
  },

  // Release version for error grouping. Native builds use native version info,
  // web/Expo Go fall back to config values. Not critical since we use debug symbols.
  release: Constants.nativeAppVersion && Constants.nativeBuildVersion
    ? `${Constants.nativeAppVersion}+${Constants.nativeBuildVersion}`
    : Constants.expoConfig?.extra?.appVersion || Constants.expoConfig?.version || undefined,
  dist: Constants.nativeBuildVersion
    || Constants.expoConfig?.ios?.buildNumber
    || Constants.expoConfig?.android?.versionCode,
});

LogBox.ignoreLogs([
  /.*: Support for defaultProps will be removed from function components in a future major release. Use JavaScript default parameters instead./,
  /React does not recognize the `dataSet` prop on a DOM element. If you intentionally want it to appear in the DOM as a custom attribute, spell it as lowercase `dataset` instead./,
  /"shadow.*" style props are deprecated. Use "boxShadow"./,
  /props.pointerEvents is deprecated. Use style.pointerEvents/,
  /SafeAreaView has been deprecated/,
]);

const isVisualLabRoute = () =>
  Platform.OS === 'web' &&
  typeof window !== 'undefined' &&
  (window.location.pathname.startsWith('/visual-lab')
    || window.location.pathname.startsWith('/orb-lab'));

const App = () => {
  const [fontsLoaded, setFontsLoaded] = React.useState(false);

  React.useEffect(() => {
    async function loadFonts() {
      try {
        await Font.loadAsync({
          'Inter-Regular': require('./assets/fonts/Inter-Regular.otf'), // weight 400
          //'Inter-Medium': require('./assets/fonts/Inter-Medium.otf'), // weight 500
          'Inter-SemiBold': require('./assets/fonts/Inter-SemiBold.otf'), // weight 600
          //'Inter-Bold': require('./assets/fonts/Inter-Bold.otf'), // weight 700
        });
      } catch (e) {
        log.warn('Font load error', e);
      } finally {
        setFontsLoaded(true);
      }
    }
    loadFonts();
  }, []);

  const initialize = useAuthStore((state) => state.initialize);
  const user = useAuthStore((state) => state.user);

  React.useEffect(() => {
    if (!isVisualLabRoute()) {
      initialize();
    }
  }, [initialize]);

  // Sync user with Sentry for better error tracking
  React.useEffect(() => {
    if (user) {
      Sentry.setUser({
        id: user.id?.toString(),
        username: user.username,
        email: user.email,
        role: user.role,
      });
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  // Hide splash screen when fonts finish loading
  React.useEffect(() => {
    if (fontsLoaded) {
      SplashScreen.hideAsync();
    }
  }, [fontsLoaded]);

  if (!fontsLoaded) {
    return null;
  }

  if (isVisualLabRoute()) {
    return (
      <GestureHandlerRootView style={{ flex: 1 }}>
        <VisualLabProviders>
          <VisualLabApp />
        </VisualLabProviders>
      </GestureHandlerRootView>
    );
  }

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <SafeAreaProvider>
        {Platform.OS === 'web' && (
          <style>{`
          ${TOAST_PORTAL_POINTER_EVENTS_CSS}

          /**
           * Chrome Autofill Background Fix:
           * Overrides Chrome's default blue autofill background with white.
           * Gluestack-style v1 doesn't support :-webkit-autofill pseudo-selectors,
           * so this must be handled via global CSS.
           */
          input:-webkit-autofill,
          input:-webkit-autofill:hover,
          input:-webkit-autofill:focus,
          input:-webkit-autofill:active {
            -webkit-box-shadow: 0 0 0 1000px #FFFFFF inset !important;
            -webkit-text-fill-color: #344054 !important;
            transition: background-color 5000s ease-in-out 0s;
          }
        `}</style>
      )}
      <QueryClientProvider client={queryClient}>
        <MathRendererProvider>
          <EnvironmentGuard>
            <TenantSettingsLoader>
              <ThemeWrapper>
                <DialogProvider>
                  <WhatsNewProvider>
                    <AppLayout>
                      <ImpersonationBar />
                      <MaintenanceGuard />
                      <MainNavigation />
                    </AppLayout>
                  </WhatsNewProvider>
                </DialogProvider>
              </ThemeWrapper>
            </TenantSettingsLoader>
          </EnvironmentGuard>
        </MathRendererProvider>
      </QueryClientProvider>
    </SafeAreaProvider>
    </GestureHandlerRootView>
  );
};

export default Sentry.wrap(App);
